import { useCallback, useEffect, useMemo, useState } from "react";
import { Building2, Power, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { Table } from "../../../components/ui/Table";
import { syncTimestampColumns } from "../../../components/ui/SyncTimestamps";
import { Badge } from "../../../components/ui/Badge";
import { FilterBar } from "../../../components/ui/FilterBar";
import { KpiCard } from "../../../components/ui/KpiCard";
import { Modal } from "../../../components/ui/Modal";
import { ConfirmModal } from "../../../components/ui/ConfirmModal";
import { Input, FormField } from "../../../components/ui/Input";
import { SectionCard } from "../../../components/ui/SectionCard";
import { BarChart } from "../../../components/ui/BarChart";
import { groupByMonth } from "../../../utils/dashboardUtils";
import api from "../../../services/api";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  PER_PAGE, ROLE_VARIANTS, fmtAmount,
  PageHeader, RowActions, FormFooter, GlobalError, ErrorBanner,
} from "../shared";
import { ClientDrawer }      from "./ClientDrawer";
import { ClientFilterModal } from "./ClientFilterModal";

const COMPANY_DEFAULTS = { name: "", erpCode: "", ice: "", adresse: "" };

const INV_STATUS = {
  pending:    { variant: "pending",   group: "pending"   },
  paid:       { variant: "paid",      group: "paid"      },
  overdue_30: { variant: "overdue30", group: "overdue"   },
  overdue_60: { variant: "overdue60", group: "overdue"   },
  cancelled:  { variant: "preparing", group: "cancelled" },
};

export default function CompaniesView() {
  const { t, i18n } = useTranslation();
  const { impersonate, hasPermission } = useAuth();
  const canManage = hasPermission('manage_companies');
  const locale = i18n.language;

  const [roles, setRoles] = useState([]);

  useEffect(() => {
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
  }, []);

  const ROLE_META = useMemo(() => {
    const meta = {};
    roles.forEach((role) => {
      meta[role.name] = {
        label: t(`roles.${role.name}`, { defaultValue: role.description || role.name }),
        variant: ROLE_VARIANTS[role.name] || "info",
      };
    });
    return meta;
  }, [roles, t]);

  const [companies, setCompanies]           = useState([]);
  const [allCompaniesRaw, setAllCompaniesRaw] = useState([]);
  const [allInvoices, setAllInvoices]       = useState([]);
  const [total, setTotal]                   = useState(0);
  const [counts, setCounts]                 = useState({ all: 0, active: 0, inactive: 0 });
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [search, setSearch]                 = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [statusFilter, setStatusFilter]     = useState("all");
  const [page, setPage]                     = useState(1);
  const [formModal, setFormModal]           = useState(null);
  const [formData, setFormData]             = useState(COMPANY_DEFAULTS);
  const [formErrors, setFormErrors]         = useState({});
  const [saving, setSaving]                 = useState(false);
  const [confirm, setConfirm]               = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [toggling, setToggling]             = useState(new Set());
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({ status: [], invoices: [] });

  const loadMeta = useCallback(() => {
    Promise.all([
      api.post("/companies/get-all?skip=0&limit=500", { type: "CLIENT" }),
      api.get("/invoices?limit=1000"),
    ]).then(([cRes, invRes]) => {
      const raw = Array.isArray(cRes.data) ? cRes.data : (cRes.data?.data ?? []);
      setAllCompaniesRaw(raw.filter((c) => c.type === "CLIENT"));
      setAllInvoices(invRes.data.rows ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const companyStats = useMemo(() => {
    const map = new Map();
    allInvoices.forEach((inv) => {
      const cid = String(inv.clientId?._id ?? inv.clientId ?? "");
      if (!cid) return;
      if (!map.has(cid)) {
        map.set(cid, { count: 0, total: 0, overdueAmt: 0, paidAmt: 0, overdueCount: 0, paidCount: 0, pendingCount: 0 });
      }
      const s   = map.get(cid);
      const amt = inv.amountTTC ?? inv.amountEur ?? 0;
      const grp = INV_STATUS[inv.status]?.group;
      s.count++;
      s.total += amt;
      if (grp === "overdue")  { s.overdueAmt += amt; s.overdueCount++; }
      if (grp === "paid")     { s.paidAmt    += amt; s.paidCount++;    }
      if (grp === "pending")  { s.pendingCount++;                       }
    });
    return map;
  }, [allInvoices]);

  const loadCompanies = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    const modalStatus = advancedFilters.status;
    if (modalStatus.length === 1) {
      params.set("status", modalStatus[0]);
    } else if (modalStatus.length === 0 && statusFilter !== "all") {
      params.set("status", statusFilter);
    }
    if (debouncedSearch.trim()) params.set("q", debouncedSearch.trim());
    if (advancedFilters.invoices.length > 0) {
      const wantPaid    = advancedFilters.invoices.includes("paid");
      const wantOverdue = advancedFilters.invoices.includes("overdue");
      const ids = [];
      companyStats.forEach((s, cid) => {
        if ((wantPaid && s.paidCount > 0) || (wantOverdue && s.overdueCount > 0)) ids.push(cid);
      });
      ids.forEach((id) => params.append("ids", id));
    }
    api.get(`/companies?${params}`)
      .then((res) => {
        setCompanies(res.data.data ?? []);
        setTotal(res.data.total ?? 0);
        if (res.data.counts) setCounts(res.data.counts);
      })
      .catch((e) => setError(e.message || t("common.error_occurred")))
      .finally(() => setLoading(false));
  }, [statusFilter, debouncedSearch, advancedFilters, companyStats, page, t]);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  function refresh() { loadMeta(); loadCompanies(); }

  const activeFilterCount = advancedFilters.status.length + advancedFilters.invoices.length;

  const kpis = useMemo(() => {
    const active   = allCompaniesRaw.filter((c) => c.isActive).length;
    const inactive = allCompaniesRaw.filter((c) => !c.isActive).length;
    let totalAmt = 0, overdueAmt = 0, paidAmt = 0;
    companyStats.forEach((s) => { totalAmt += s.total; overdueAmt += s.overdueAmt; paidAmt += s.paidAmt; });
    return { total: allCompaniesRaw.length, active, inactive, totalAmt, overdueAmt, paidAmt };
  }, [allCompaniesRaw, companyStats]);

  const monthlyData = useMemo(() => groupByMonth(allCompaniesRaw, "createdAt"), [allCompaniesRaw]);

  function openAdd() {
    setFormData(COMPANY_DEFAULTS);
    setFormErrors({});
    setFormModal({ mode: "create" });
  }

  function openEdit(row) {
    setFormData({ name: row.name || "", erpCode: row.erpCode || "", ice: row.ice || "", adresse: row.adresse || "" });
    setFormErrors({});
    setFormModal({ mode: "edit", id: row._id });
  }

  function field(key, val) {
    setFormData((d) => ({ ...d, [key]: val }));
    if (formErrors[key]) setFormErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate() {
    const errs = {};
    if (!formData.name.trim()) errs.name = t("common.required_field");
    setFormErrors(errs);
    return !errs.name;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        name:    formData.name,
        type:    "CLIENT",
        erpCode: formData.erpCode || undefined,
        ice:     formData.ice     || undefined,
        adresse: formData.adresse || undefined,
      };
      if (formModal.mode === "create") {
        await api.post("/companies", { ...payload, isActive: true });
      } else {
        await api.put(`/companies/${formModal.id}`, payload);
      }
      setFormModal(null);
      refresh();
    } catch (e) {
      setFormErrors({ _global: e.response?.data?.message || e.message || t("common.error_occurred") });
    } finally {
      setSaving(false);
    }
  }

  const handleToggleStatus = useCallback(async (company) => {
    if (!canManage) return;
    const id = String(company._id);
    setToggling((prev) => new Set([...prev, id]));
    try {
      await api.put(`/companies/${company._id}`, { isActive: !company.isActive });
      setCompanies((prev) => prev.map((c) => String(c._id) === id ? { ...c, isActive: !c.isActive } : c));
      if (selectedCompany && String(selectedCompany._id) === id) {
        setSelectedCompany((s) => ({ ...s, isActive: !s.isActive }));
      }
    } catch {
      // silently fail
    } finally {
      setToggling((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, [selectedCompany, canManage]);

  async function handleDelete() {
    if (!confirm || !canManage) return;
    setSaving(true);
    try {
      await api.delete(`/companies/${confirm._id}`);
      setConfirm(null);
      refresh();
    } catch {
      // stay open on error
    } finally {
      setSaving(false);
    }
  }

  const columns = useMemo(() => [
    {
      key: "name",
      label: t("companies.col_company"),
      render: (v, row) => (
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-primary/10 grid place-items-center flex-shrink-0">
            <Building2 size={13} strokeWidth={1.75} className="text-primary" />
          </div>
          <div>
            <p className="font-medium text-slate-800">{v}</p>
            {row.erpCode && <p className="text-[11px] text-slate-400 font-mono">{row.erpCode}</p>}
          </div>
        </div>
      ),
    },
    {
      key: "ice",
      label: t("companies.col_ice"),
      render: (v) => <span className="font-mono text-[12px] text-slate-500">{v || "—"}</span>,
    },
    {
      key: "adresse",
      label: t("companies.col_address"),
      render: (v) => <span className="text-slate-600 text-[12px]">{v || "—"}</span>,
    },
    {
      key: "_inv",
      label: "Factures",
      render: (_, row) => {
        const s = companyStats.get(String(row._id));
        if (!s?.count) return <span className="text-slate-300 text-[12px]">—</span>;
        return (
          <div>
            <p className="text-[12px] font-medium text-slate-700">{s.count} facture{s.count !== 1 ? "s" : ""}</p>
            {s.overdueAmt > 0 ? (
              <p className="text-[11px] text-rose-500 font-mono">{fmtAmount(s.overdueAmt, "EUR", locale)} en retard</p>
            ) : (
              <p className="text-[11px] text-slate-400 font-mono">{fmtAmount(s.total, "EUR", locale)}</p>
            )}
          </div>
        );
      },
    },
    {
      key: "isActive",
      label: t("companies.col_status"),
      render: (v, row) => {
        const isTogglingRow = toggling.has(String(row._id));
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleToggleStatus(row); }}
            disabled={isTogglingRow}
            className="flex items-center gap-1.5 group disabled:opacity-60"
            title={v ? "Cliquer pour désactiver" : "Cliquer pour activer"}
          >
            <Badge variant={v ? "success" : "preparing"}>
              {v ? t("common.active") : t("common.inactive")}
            </Badge>
            <Power size={10} strokeWidth={2} className={`transition ${
              isTogglingRow
                ? "animate-pulse text-slate-400 opacity-70"
                : "opacity-0 group-hover:opacity-50 text-slate-400"
            }`} />
          </button>
        );
      },
    },
    ...syncTimestampColumns(t, locale),
    {
      key: "_act",
      label: "",
      align: "right",
      render: (_, row) => canManage ? (
        <RowActions onEdit={() => openEdit(row)} onDelete={() => setConfirm(row)} />
      ) : null,
    },
  ], [t, locale, companyStats, toggling, handleToggleStatus, canManage]);

  const isEditing = formModal?.mode === "edit";

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("companies.title")}
        sub={t("companies.count_other", { count: counts.all })}
        onRefresh={refresh}
        loading={loading}
        onAdd={canManage ? openAdd : undefined}
        addLabel={t("companies.add_company")}
      />

      {error && <ErrorBanner message={error} onRetry={refresh} />}

      {!loading && allCompaniesRaw.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <KpiCard label={t("companies.kpi_total")}    value={kpis.total}    icon={Building2} sparkline={monthlyData.map((d) => d.value)} />
          <KpiCard label={t("companies.kpi_active")}   value={kpis.active}   sub={kpis.total ? `${Math.round((kpis.active / kpis.total) * 100)} % du total` : "—"} trend={kpis.active > 0 ? "up" : undefined} />
          <KpiCard label={t("companies.kpi_inactive")} value={kpis.inactive} />
        </div>
      )}

      {!loading && allCompaniesRaw.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <SectionCard title="Nouveaux clients par mois" className="col-span-2">
            <BarChart data={monthlyData} height={140} />
          </SectionCard>
          <SectionCard title="Répartition portefeuille">
            <div className="space-y-3">
              {[
                { label: "Clients actifs",   value: kpis.active,   total: kpis.total, color: "#059669" },
                { label: "Clients inactifs", value: kpis.inactive, total: kpis.total, color: "#94a3b8" },
              ].map((row) => {
                const pct = row.total > 0 ? Math.round((row.value / row.total) * 100) : 0;
                return (
                  <div key={row.label}>
                    <div className="flex justify-between text-[11.5px] mb-1">
                      <span className="text-slate-600">{row.label}</span>
                      <span className="font-semibold text-slate-800">
                        {row.value}<span className="text-slate-400 font-normal ml-1">({pct} %)</span>
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${pct}%`, backgroundColor: row.color }}
                      />
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-slate-100 pt-3 space-y-1">
                <div className="flex justify-between text-[11.5px]">
                  <span className="text-slate-500">Total facturé</span>
                  <span className="font-semibold text-slate-700 font-mono text-[11px]">
                    {fmtAmount(kpis.totalAmt, "EUR", locale)}
                  </span>
                </div>
                {kpis.overdueAmt > 0 && (
                  <div className="flex justify-between text-[11.5px]">
                    <span className="text-rose-500">En retard</span>
                    <span className="font-semibold text-rose-500 font-mono text-[11px]">
                      {fmtAmount(kpis.overdueAmt, "EUR", locale)}
                    </span>
                  </div>
                )}
                {kpis.paidAmt > 0 && (
                  <div className="flex justify-between text-[11.5px]">
                    <span className="text-emerald-600">Encaissé</span>
                    <span className="font-semibold text-emerald-600 font-mono text-[11px]">
                      {fmtAmount(kpis.paidAmt, "EUR", locale)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </SectionCard>
        </div>
      )}

      <FilterBar
        groups={[{
          value: statusFilter,
          onChange: (k) => { setStatusFilter(k); setAdvancedFilters((f) => ({ ...f, status: [] })); setPage(1); },
          tabs: [
            { key: "all",      label: t("companies.filter_all"),      count: counts.all      },
            { key: "active",   label: t("companies.filter_active"),   count: counts.active   },
            { key: "inactive", label: t("companies.filter_inactive"), count: counts.inactive },
          ],
        }]}
        search={search}
        onSearch={(e) => { setSearch(e.target.value); setPage(1); }}
        searchPlaceholder={t("companies.search_placeholder")}
        actions={
          <button
            onClick={() => setShowFilterModal(true)}
            className={`relative h-9 px-3 flex items-center gap-1.5 rounded-lg border text-[12px] font-medium transition ${
              activeFilterCount > 0
                ? "border-primary bg-primary/5 text-primary"
                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            <SlidersHorizontal size={13} strokeWidth={1.75} />
            Filtres
            {activeFilterCount > 0 && (
              <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        }
      />

      <ClientFilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        values={advancedFilters}
        onApply={(draft) => {
          setAdvancedFilters(draft);
          if (draft.status.length > 0) setStatusFilter("all");
          setPage(1);
        }}
        onReset={() => { setAdvancedFilters({ status: [], invoices: [] }); setPage(1); }}
      />

      <Table
        columns={columns}
        rows={companies}
        loading={loading}
        onRowClick={(row) => setSelectedCompany(row)}
        emptyState={
          <div className="py-16 flex flex-col items-center gap-3 text-slate-400">
            <Building2 size={32} strokeWidth={1.25} className="opacity-40" />
            <p className="text-[13px]">{t("companies.empty")}</p>
          </div>
        }
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <ClientDrawer
        company={selectedCompany}
        onClose={() => setSelectedCompany(null)}
        onEdit={() => { openEdit(selectedCompany); setSelectedCompany(null); }}
        onDelete={() => { setConfirm(selectedCompany); setSelectedCompany(null); }}
        onToggleStatus={handleToggleStatus}
        onImpersonate={async (companyId) => { setSelectedCompany(null); await impersonate(companyId); }}
        allInvoices={allInvoices}
        companyStats={companyStats}
        ROLE_META={ROLE_META}
        locale={locale}
        canManage={canManage}
      />

      <Modal
        open={!!formModal}
        onClose={() => setFormModal(null)}
        title={isEditing ? t("companies.form_edit_title") : t("companies.form_add_title")}
        size="md"
        footer={<FormFooter onCancel={() => setFormModal(null)} onSave={handleSave} saving={saving} />}
      >
        {formErrors._global && <GlobalError message={formErrors._global} />}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <FormField label={t("companies.name_label")} required helper={formErrors.name} error={!!formErrors.name}>
              <Input
                value={formData.name}
                onChange={(e) => field("name", e.target.value)}
                placeholder="Delassus SA"
                state={formErrors.name ? "error" : "default"}
              />
            </FormField>
          </div>
          <FormField label={t("companies.erp_label")}>
            <Input value={formData.erpCode} onChange={(e) => field("erpCode", e.target.value)} placeholder="C00123" />
          </FormField>
          <FormField label={t("companies.ice_label")}>
            <Input value={formData.ice} onChange={(e) => field("ice", e.target.value)} placeholder="002145871000048" />
          </FormField>
          <div className="col-span-2">
            <FormField label={t("companies.address_label")}>
              <Input value={formData.adresse} onChange={(e) => field("adresse", e.target.value)} placeholder="Zone Souss, 80000 Agadir, Maroc" />
            </FormField>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={handleDelete}
        loading={saving}
        title={t("companies.delete_title", { name: confirm?.name })}
        message={t("companies.delete_message")}
        confirmLabel={t("companies.delete_confirm")}
      />
    </div>
  );
}
